import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import {
  deleteWorkOrderAttachment,
  downloadWorkOrderAttachment,
  getWorkOrderAttachments,
  uploadWorkOrderAttachment,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { formatFileSize, formatTimestamp } from "../../cmms/cmmsFormat";
import { checkUploadAgainstLimits, useCmmsUploadLimits } from "../../../hooks/useCmmsUploadLimits";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../../cmms/assetHierarchyStyles";

export default function WorkOrderAttachmentList({ workOrderId, companyKey, userNameById }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const opFailed = intl.formatMessage(messages.operationFailed);
  const fileInputRef = useRef(null);

  const [uploadProgress, setUploadProgress] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const attachmentsQuery = useQuery(
    ["cmmsWorkOrderAttachments", companyKey, workOrderId],
    () => getWorkOrderAttachments(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const uploadMutation = useMutation({
    mutationFn: (file) => uploadWorkOrderAttachment(workOrderId, {
      companyKey,
      file,
      onProgress: (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId) => deleteWorkOrderAttachment(companyKey, workOrderId, attachmentId),
  });

  const attachments = (attachmentsQuery.data ?? [])
    .slice()
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  const uploadLimits = useCmmsUploadLimits();

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // The server rejects oversized and disallowed files; checking here first saves the
    // round trip and gives a clearer message than the API error.
    const rejection = checkUploadAgainstLimits(file, uploadLimits);
    if (rejection) {
      pushErrorMessage(rejection === "size"
        ? intl.formatMessage(messages.attachmentsTooLarge, {
            name: file.name,
            max: formatFileSize(uploadLimits.maxBytes),
          })
        : intl.formatMessage(messages.attachmentsTypeNotAllowed, {
            name: file.name,
            type: file.type || intl.formatMessage(messages.attachmentsTypeUnknown),
          }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadProgress(0);
    try {
      await uploadMutation.mutateAsync(file);
      pushNotification(intl.formatMessage(messages.toastFileUploaded));
      await attachmentsQuery.refetch();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (attachment) => {
    try {
      await downloadWorkOrderAttachment(companyKey, workOrderId, attachment.id, attachment.fileName);
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, opFailed));
    }
  };

  const handleDelete = (attachment) => {
    setConfirmation({
      title: intl.formatMessage(messages.attachmentsDeleteTitle),
      message: intl.formatMessage(messages.attachmentsDeleteMessage, { fileName: attachment.fileName }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(attachment.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastAttachmentDeleted));
          await attachmentsQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, opFailed));
        }
      },
    });
  };

  // react-query v4 exposes isLoading on mutations; isPending is the v5 name. Keep
  // both so the guard works before and after an upgrade.
  const isMutating = uploadMutation.isPending || uploadMutation.isLoading
    || deleteMutation.isPending || deleteMutation.isLoading;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.attachmentsCount} values={{ count: attachments.length }} />
        </Typography>
        <Button size="small" variant="contained" startIcon={<CloudUploadIcon />} onClick={handleUploadClick} disabled={isMutating} sx={getContainedButtonSx(cmmsColors)}>
          <FormattedMessage {...messages.attachmentsUpload} />
        </Button>
        <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
      </Stack>

      {uploadProgress !== null &&
        <Box sx={{ mb: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.attachmentsProgress} values={{ percent: uploadProgress }} />
          </Typography>
        </Box>
      }

      {attachmentsQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {attachmentsQuery.error &&
        <CmmsErrorState error={attachmentsQuery.error} fallback={opFailed} onRetry={() => attachmentsQuery.refetch()} isRetrying={attachmentsQuery.isFetching} dense />
      }
      {!attachmentsQuery.isLoading && !attachmentsQuery.error && attachments.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          <FormattedMessage {...messages.attachmentsEmpty} />
        </Typography>
      }
      {attachments.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage {...messages.attachmentsColumnFile} /></TableCell>
              <TableCell><FormattedMessage {...messages.attachmentsColumnUploadedBy} /></TableCell>
              <TableCell><FormattedMessage {...messages.attachmentsColumnUploadedAt} /></TableCell>
              <TableCell sx={{ width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments.map((attachment) => {
              const uploader = userNameById?.get(attachment.uploadedByUserId) ?? `#${attachment.uploadedByUserId}`;
              return (
                <TableRow key={attachment.id} hover>
                  <TableCell sx={{ wordBreak: "break-all" }}>{attachment.fileName}</TableCell>
                  <TableCell>{uploader}</TableCell>
                  <TableCell>{formatTimestamp(intl, attachment.uploadedAt)}</TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label={intl.formatMessage(messages.attachmentsDownloadAria)} onClick={() => handleDownload(attachment)}>
                      <DownloadIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" aria-label={intl.formatMessage(messages.attachmentsDeleteAria)} onClick={() => handleDelete(attachment)}>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
